import {
  getEncryptionKey,
  getExchangeEncryptionKey,
} from '@/api/encryption.api';
import apiConfig from '@/config/api.config';
import type { EncryptorType } from '@/interfaces/systemConfig.interface';
import {
  XQEncryptData,
  XQDecryptData,
  decryptXQValueAuto,
} from './xqEncryption.helpers';

type EncryptionDirection = 'request' | 'response' | 'both';

/**
 * Encryption/Decryption function mapper
 * Maps encryptor types to their respective encryption/decryption functions
 */
const encryptorMap = {
  XQ: {
    encrypt: XQEncryptData,
    decrypt: XQDecryptData,
  },
  // Future encryptors can be added here
} as const;

/**
 * Decrypt "ENC:...:ENC" property values by encryptor type.
 * Maps encryptor to provider-specific decrypt (e.g. XQ auto/OTP).
 * Add more providers here when supported.
 */
const decryptValueAutoMap = {
  XQ: decryptXQValueAuto,
} as const;

/** Encryptor types that support in-UI decrypt (key/lock icon). Add here when a new encryptor gets decrypt UI. */
export const ENCRYPTORS_WITH_DECRYPT_UI = ['XQ'] as const;

/**
 * Returns true when the given encryptor supports the in-place decrypt UI (key/lock icon in properties and table).
 */
export function shouldShowDecryptUI(encryptor: string | undefined): boolean {
  return (
    encryptor != null &&
    (ENCRYPTORS_WITH_DECRYPT_UI as readonly string[]).includes(encryptor)
  );
}

const ENCRYPTION_API_PATH_MARKER = '/encryption/';
const QUERY_ENDPOINT_PATH_MARKER = '/query';
const SUPPRESSIBLE_QUERY_STATUSES = new Set<number>([400, 502]);

/**
 * Master switch for the demo-only error toast / inline-error suppression.
 * Read by every site that decides whether to mute XQ encryption failures
 * and 400/502 failures from `*\/query` endpoints.
 *
 * TODO(demo-cleanup): remove together with `isDemoSuppressibleError` and
 * every consumer's `if (SUPPRESS_DEMO_TOASTS && …)` branch once the
 * backend is stabilized.
 */
export const SUPPRESS_DEMO_TOASTS = true;

/**
 * True when the failure originated from the XQ encryption HTTP endpoints
 * (`/encryption/*`) or from request encryption wrapping in this module.
 *
 * TODO(demo-cleanup): remove after backend XQ endpoints are stabilized.
 */
export const isEncryptionPipelineError = (error: unknown): boolean => {
  if (error == null) return false;

  const message =
    error instanceof Error
      ? error.message
      : typeof (error as { message?: unknown }).message === 'string'
        ? String((error as { message: string }).message)
        : '';

  if (message.startsWith('Failed to encrypt request')) {
    return true;
  }

  const err = error as {
    response?: { config?: { url?: string } };
    config?: { url?: string };
  };
  const url = err.response?.config?.url ?? err.config?.url;
  if (typeof url === 'string' && url.includes(ENCRYPTION_API_PATH_MARKER)) {
    return true;
  }

  return false;
};

/**
 * True when the failure is one of the demo-suppressible error categories:
 * - XQ encryption pipeline failures (see `isEncryptionPipelineError`)
 * - 400/502 failures from any data query endpoint (`*\/query`, including
 *   `/graph/query`)
 * - No-response (network-class) failures from any data query endpoint —
 *   covers the OIDC service-worker race on dashboard open where
 *   concurrent fetches can throw `Failed to fetch` mid token-renew.
 *
 * TODO(demo-cleanup): remove after backend is stabilized.
 */
export const isDemoSuppressibleError = (error: unknown): boolean => {
  if (isEncryptionPipelineError(error)) {
    return true;
  }

  if (error == null || typeof error !== 'object') {
    return false;
  }

  const err = error as {
    response?: { status?: number; config?: { url?: string } };
    config?: { url?: string };
  };
  const status = err.response?.status;
  const url = err.response?.config?.url ?? err.config?.url;
  const isQueryUrl =
    typeof url === 'string' && url.includes(QUERY_ENDPOINT_PATH_MARKER);

  if (
    typeof status === 'number' &&
    SUPPRESSIBLE_QUERY_STATUSES.has(status) &&
    isQueryUrl
  ) {
    return true;
  }

  // No `response` means axios never got one — typically the OIDC service
  // worker throwing `Failed to fetch` during a token-renew race. Suppress
  // only on data query URLs so other no-response errors stay visible.
  if (err.response === undefined && isQueryUrl) {
    return true;
  }

  return false;
};

/**
 * Encrypts JSON/text data using the specified encryptor
 * @param data - The data to encrypt (object will be JSON stringified)
 * @param key - The encryption key
 * @param encryptor - The encryptor type to use
 * @returns Promise<string> - The encrypted data as base64 string
 */
export const encryptData = async (
  data: any,
  key: string,
  encryptor: EncryptorType,
  shouldPrefixKey: boolean = true,
): Promise<string> => {
  const encryptFunction = encryptorMap[encryptor]?.encrypt;

  if (!encryptFunction) {
    throw new Error(
      `Unsupported encryptor: ${encryptor}. Supported encryptors: ${Object.keys(
        encryptorMap,
      ).join(', ')}`,
    );
  }

  return encryptFunction(data, key, shouldPrefixKey);
};

/**
 * Decrypts encrypted data using the specified encryptor
 * @param encryptedData - The encrypted data (base64 string or array)
 * @param key - The encryption key
 * @param encryptor - The encryptor type to use
 * @returns Promise<any> - The decrypted data (parsed JSON if valid JSON, otherwise string)
 */
export const decryptData = async (
  encryptedData: string,
  key: string,
  encryptor: EncryptorType,
  shouldPrefixKey: boolean = true,
): Promise<any> => {
  const decryptFunction = encryptorMap[encryptor]?.decrypt;

  if (!decryptFunction) {
    throw new Error(
      `Unsupported encryptor: ${encryptor}. Supported encryptors: ${Object.keys(
        encryptorMap,
      ).join(', ')}`,
    );
  }

  return decryptFunction(encryptedData, key, shouldPrefixKey);
};

/**
 * Encrypts request payload and prepares encryption headers
 * Handles the complete request encryption flow for axios interceptors
 * @param requestData - The request data to encrypt (object)
 * @param encryptor - The encryptor type to use
 * @returns Promise with encrypted data (as JSON string) and encryption headers, or null if encryption fails
 */
export const encryptRequestPayload = async (
  requestData: any,
  encryptor: EncryptorType,
): Promise<{ encryptedData: string; headers: Record<string, string> }> => {
  try {
    // Get encryption key
    const encryptionConfig = await getEncryptionKey();

    if (!encryptionConfig.data) {
      return { encryptedData: JSON.stringify(requestData), headers: {} };
    }

    // Encrypt request body using the specified encryptor
    const encryptedData = await encryptData(
      requestData,
      encryptionConfig.data.key,
      encryptor,
    );

    // Prepare encryption headers
    const headers: Record<string, string> = {
      'X-Decrypt-Exchange-Key': encryptionConfig.data.exchangeKey,
    };

    // Get exchange encryption key for response decryption
    const exchangeEncryptionKeyResponse = await getExchangeEncryptionKey();
    if (exchangeEncryptionKeyResponse.data) {
      headers['X-Encrypt-Exchange-Key'] =
        exchangeEncryptionKeyResponse.data.exchangeKey;
    }

    // Return JSON-stringified encrypted data to match fetch implementation
    // This ensures the encrypted string is sent as a JSON-encoded string
    return {
      encryptedData: JSON.stringify(encryptedData),
      headers,
    };
  } catch (error) {
    throw new Error(`Failed to encrypt request: ${error}`);
  }
};

/**
 * Decrypts response payload using exchange key from headers
 * Handles the complete response decryption flow for axios interceptors
 * @param responseData - The response data to decrypt (can be string, object, or other)
 * @param exchangeEncryptionKeyValue - The exchange key from response headers
 * @param encryptor - The encryptor type to use
 * @returns Promise with decrypted data (parsed JSON if valid, otherwise string), or original data if decryption fails
 */
export const decryptResponsePayload = async (
  responseData: any,
  exchangeEncryptionKeyValue: string | null,
  encryptor: EncryptorType,
): Promise<any> => {
  if (!exchangeEncryptionKeyValue || !responseData) {
    return responseData;
  }

  try {
    // Prepare data for decryption - handle different response data types
    let dataToDecrypt: string;
    if (typeof responseData === 'string') {
      dataToDecrypt = responseData;
    } else if (typeof responseData === 'object') {
      // If it's an object, try to stringify it (though encrypted data should be a string)
      dataToDecrypt = JSON.stringify(responseData);
    } else {
      // For other types, convert to string
      dataToDecrypt = String(responseData);
    }

    // Decrypt response data using the specified encryptor
    const decryptedData = await decryptData(
      dataToDecrypt,
      exchangeEncryptionKeyValue,
      encryptor,
    );

    // Parse decrypted data if it's JSON
    try {
      return JSON.parse(decryptedData);
    } catch {
      // If not JSON, use as string
      return decryptedData;
    }
  } catch (error) {
    console.error('Failed to decrypt response payload:', error);
    // Return original data if decryption fails
    return responseData;
  }
};

/**
 * Checks if encryption should be applied based on API configuration
 * Iterates over all API configurations to find matching patterns
 * @param method - The HTTP method (GET, POST, etc.)
 * @param url - The request URL (relative path)
 * @param direction - The encryption direction ('request' or 'response')
 * @returns true if encryption should be applied, false otherwise
 */
export const shouldApplyEncryptionBasedOnApiConfig = (
  method: string,
  url: string,
  direction: 'request' | 'response',
): boolean => {
  // Prepend gateway prefix to match patterns
  const fullUrl = '/gateway/api/v1'.concat(url);
  const upperMethod = method.toUpperCase();

  // Iterate over all API configurations
  for (const apiName in apiConfig) {
    const api = apiConfig[apiName as keyof typeof apiConfig];

    // Skip if encryption is not enabled for this API
    if (!api?.encryption?.enabled) {
      continue;
    }

    // Check patterns for this API
    const patterns = api.encryption.patterns || [];

    for (const pattern of patterns) {
      // Check if method matches
      const methods = pattern.methods || [];
      if (!methods.includes(upperMethod)) {
        continue;
      }

      // Check if direction matches
      const patternDirection = (pattern.direction ||
        'both') as EncryptionDirection;
      if (patternDirection !== 'both' && patternDirection !== direction) {
        continue;
      }

      // Check if URL matches the path pattern
      // path_pattern is a precompiled RegExp object
      const pathPattern = pattern.path_pattern;
      if (pathPattern && pathPattern.test(fullUrl)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Checks if a value matches the encrypted pattern ENC:{value}:ENC
 * @param value - The value to check (can be any type)
 * @returns true if the value matches the encrypted pattern, false otherwise
 */
export const isEncryptedValue = (value: any): boolean => {
  if (typeof value !== 'string') return false;
  return /^ENC:.+:ENC$/.test(value);
};

/**
 * Extracts the encrypted value from the pattern ENC:{value}:ENC
 * @param value - The encrypted value string with the ENC: prefix and :ENC suffix
 * @returns The extracted encrypted value without the ENC: markers, or empty string if pattern doesn't match
 */
export const extractEncryptedValue = (value: string): string => {
  const match = value.match(/^ENC:(.+):ENC$/);
  return match ? match[1] : '';
};

/**
 * Decrypts an encrypted value (e.g. ENC:base64:ENC) using the configured encryptor.
 * Uses provider-specific logic (XQ auto/OTP, etc.) via decryptValueAutoMap.
 * @param encryptedDataBase64 - Base64 encoded encrypted data (without ENC: wrapper)
 * @param encryptor - The encryptor type (e.g. 'XQ')
 * @returns Promise with decrypted data
 */
export const decryptValueAuto = async (
  encryptedDataBase64: string,
  encryptor: EncryptorType,
): Promise<any> => {
  const decryptFn = decryptValueAutoMap[encryptor];
  if (!decryptFn) {
    throw new Error(
      `Decrypt not supported for encryptor: ${encryptor}. Supported: ${Object.keys(
        decryptValueAutoMap,
      ).join(', ')}`,
    );
  }
  return decryptFn(encryptedDataBase64);
};
